import React, { useEffect } from "react";
import {
  Grid,
  Box,
  Stack,
  Typography,
  Divider,
  TextField,
  InputLabel,
  FormControl,
  Button,
  TextareaAutosize,
  FormLabel,
  FormHelperText,
  Switch,
  FormGroup,
  FormControlLabel,
  useTheme,
} from "@mui/material";
import Item from "../../Template/Item";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { object, TypeOf, z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { addSettings } from "../../redux/actions/settings.actions";
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import { green } from "@mui/material/colors";
import { storeStateType } from "../../redux/store";
import { siteSettingsType } from "../../redux/reducers/settings.reducer";
import TimesheetConfiguration from "./TimesheetConfiguration";

const siteSchema = object({
  site_title: z.string().min(1, { message: "Site title can't be empty" }),
  site_contact_email: z
    .string()
    .min(1, { message: "Contact email can't be emplty" })
    .regex(
      /^(\s?[^\s,]+@[^\s,]+\.[^\s,]+\s?,)*(\s?[^\s,]+@[^\s,]+\.[^\s,]+)$/,
      "Invalid contcat email address."
    ),
  site_contact_number: z
    .string()
    .min(1, { message: "Contact number can't be empty" }),
  site_contact_address: z
    .string()
    .min(1, { message: "Contact address can't be empty" }),
  site_website: z.string(),
  site_logo: z.any(z.instanceof(File)).optional(),
  terms_cond: z.string(),
  api_resource_id: z.string(),
  site_session_timeout: z
    .string()
    .regex(/^[1-9]\d*$/, "Invalid session out time."),
  metadata: z.string(),
  employee_role: z.string(),
  auto_activate_employee: z.boolean(),
  user_role: z.string(),
  enable_visa_type_info: z.boolean(),
});

const employeeSchema = z.object({
  employee_prefix: z.string(),
  employee_idrange: z.string().refine((val) => !isNaN(parseInt(val)), {
    message: "Expected number, received a astring",
  }),
});

const timeSheetSchema = object({
  morning_shift: z
    .string()
    .min(1, { message: "Morning Shift can't be empty" })
    .regex(/^ *((\d *- *\d)|(\d{2} *- *\d{2})) *$/, "Invalid Shift"),
  evening_shift: z
    .string()
    .min(1, { message: "Evening Shift can't be emplty" })
    .regex(/^ *((\d *- *\d)|(\d{2} *- *\d{2})) *$/, "Invalid Shift"),
  night_shift: z
    .string()
    .min(1, { message: "Night Shift can't be empty" })
    .regex(/^ *((\d *- *\d)|(\d{2} *- *\d{2})) *$/, "Invalid Shift"),
  start_day: z.number(),
  working_hours_limit: z.string(),
});

const appSchema = object({
  primaryColor: z.string().min(1, { message: "This field can't be empty" }),
  successColor: z.string().min(1, { message: "This field can't be empty" }),
  errorColor: z.string().min(1, { message: "This field can't be empty" }),
  infoColor: z.string().min(1, { message: "This field can't be empty" }),
  textColor: z.string().min(1, { message: "This field can't be empty" }),
  tabBackgroundColor: z
    .string()
    .min(1, { message: "This field can't be empty" }),
  tabActiveTextColor: z
    .string()
    .min(1, { message: "This field can't be empty" }),
  tabInactiveTextColor: z
    .string()
    .min(1, { message: "This field can't be empty" }),
  timerBackgroundColor: z
    .string()
    .min(1, { message: "This field can't be empty" }),
  timerTextColor: z.string().min(1, { message: "This field can't be empty" }),
  app_logo: z.any(z.instanceof(File)).optional(),
  api_key: z.string().min(1, { message: "This field can't be empty" }),
});

export type SiteForm = TypeOf<typeof siteSchema>;
export type EmployeeForm = TypeOf<typeof employeeSchema>;
export type TimeSheetForm = TypeOf<typeof timeSheetSchema>;
export type AppForm = TypeOf<typeof appSchema>;

const weekDaysName = [
  { id: 1, name: "Monday" },
  { id: 2, name: "Tuesday" },
  { id: 3, name: "Wednesday" },
  { id: 4, name: "Thursday" },
  { id: 5, name: "Friday" },
  { id: 6, name: "Saturday" },
  { id: 7, name: "Sunday" },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
      style={{ width: "80%" }}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

export default function Siteconfiguration() {
  const dispatch = useDispatch();
  const [value, setValue] = React.useState(0);

  const theme = useTheme();

  const { settings, loading } = useSelector(
    (state: storeStateType) => state.sitesetting
  );
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const {
    register: registerSite,
    control: siteControl,
    reset: resetSite,
    formState: { errors: siteErrors },
    handleSubmit: handleSiteSubmit,
  } = useForm<SiteForm>({ resolver: zodResolver(siteSchema) });

  const {
    register: registerEmployee,
    reset: resetEmployee,
    formState: { errors: employeeErrors },
    handleSubmit: handleEmployeeSubmit,
  } = useForm<EmployeeForm>({ resolver: zodResolver(employeeSchema) });

  const {
    register: registerTimesheet,
    unregister: unregisterTimesheet,
    reset: resetTimesheet,
    control: controlTimesheet,
    formState: { errors: timesheeErrors },
    handleSubmit: handleTimesheetSubmit,
  } = useForm<TimeSheetForm>({ resolver: zodResolver(timeSheetSchema) });

  const {
    register: registerApp,
    reset: resetApp,
    formState: { errors: appErrors },
    handleSubmit: handleAppSubmit,
  } = useForm<AppForm>({ resolver: zodResolver(appSchema) });

  const [siteLogo, setSiteLogo] = React.useState(null);
  const [appLogo, setAppLogo] = React.useState(null);

  const [employeeDisabledFields] = React.useState<string[]>([]);

  //const [color, setColor] = React.useState({});

  useEffect(() => {
    //dispatch<any>(loadSettings())
  }, [dispatch]);

  useEffect(() => {
    if (settings) {
      settings.forEach((config: siteSettingsType) => {
        if (config["config_name"] === "site") {
          resetSite(JSON.parse(config["config_value"]));
          const site_config_value = JSON.parse(config["config_value"]);
          setSiteLogo(site_config_value["site_logo"]);
        }
        if (config["config_name"] === "employee") {
          // setEmployeeDisabledFields(config['disabled']); //anish was active i dont know what this is for
          resetEmployee(JSON.parse(config["config_value"]));
        }
        if (config["config_name"] === "timesheet") {
          resetTimesheet(JSON.parse(config["config_value"]));
        }
        if (config["config_name"] === "app") {
          resetApp(JSON.parse(config["config_value"]));
          const app_config_value = JSON.parse(config["config_value"]);
          setAppLogo(app_config_value["app_logo"]);
        }
      });
    }
  }, [
    settings,
    resetApp,
    resetTimesheet,
    resetSite,
    resetEmployee,

    unregisterTimesheet,
  ]);

  const onSiteFormSubmit: SubmitHandler<SiteForm> = (data) => {
    const formData = new FormData();
    formData.append("config_name", "site");
    const config_value = {
      site_title: data.site_title,
      site_contact_number: data.site_contact_number,
      site_contact_email: data.site_contact_email,
      site_contact_address: data.site_contact_address,
      site_website: data.site_website,
      terms_cond: data.terms_cond,
      api_resource_id: data.api_resource_id,
      site_session_timeout: data.site_session_timeout,
      metadata: data.metadata,
      employee_role: data.employee_role,
      auto_activate_employee: data.auto_activate_employee,
      enable_visa_type_info: data.enable_visa_type_info,
      user_role: data.user_role,
    };
    if (data.site_logo && data.site_logo.length > 0) {
      const inputFile = document.getElementById(
        "site-logo"
      ) as HTMLInputElement;
      formData.append("site_logo", inputFile?.files?.item(0) as File);
    }

    formData.append("config_value", JSON.stringify(config_value));
    dispatch<any>(addSettings(formData));
  };

  const onEmployeeFormSubmit: SubmitHandler<EmployeeForm> = (data) => {
    const formData = new FormData();
    formData.append("config_name", "employee");
    const config_value = {
      employee_prefix: data.employee_prefix,
      employee_idrange: data.employee_idrange,
    };
    formData.append("config_value", JSON.stringify(config_value));
    dispatch<any>(addSettings(formData));
  };

  const onTimesheetFormSubmit: SubmitHandler<TimeSheetForm> = (data) => {
    const formData = new FormData();
    formData.append("config_name", "timesheet");
    let config_value = {};

    config_value = {
      morning_shift: data.morning_shift,
      evening_shift: data.evening_shift,
      night_shift: data.night_shift,
      start_day: data.start_day,
      working_hours_limit: data.working_hours_limit,
    };
    formData.append("config_value", JSON.stringify(config_value));
    dispatch<any>(addSettings(formData));
  };

  const onAppFormSubmit: SubmitHandler<AppForm> = (data) => {
    const formData = new FormData();
    formData.append("config_name", "app");
    const config_value = {
      primaryColor: data.primaryColor,
      successColor: data.successColor,
      errorColor: data.errorColor,
      infoColor: data.infoColor,
      textColor: data.textColor,
      tabBackgroundColor: data.tabBackgroundColor,
      tabActiveTextColor: data.tabActiveTextColor,
      tabInactiveTextColor: data.tabInactiveTextColor,
      timerBackgroundColor: data.timerBackgroundColor,
      timerTextColor: data.timerTextColor,
      app_logo: data.app_logo,
      api_key: data.api_key,
    };

    if (data.app_logo && data.app_logo.length > 0) {
      const inputFile = document.getElementById("app-logo") as HTMLInputElement;
      formData.append("app_logo", inputFile?.files?.item(0) as File);
    }
    formData.append("config_value", JSON.stringify(config_value));
    dispatch<any>(addSettings(formData));
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Item>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={5}
          >
            <Typography variant="h5" gutterBottom>
              Configurations
            </Typography>
            <Box
              display="flex"
              justifyContent="flex-start"
              alignItems="flex-start"
            ></Box>
          </Stack>
          <Divider />
          <Box
            sx={{
              flexGrow: 1,
              bgcolor: "background.paper",
              display: "flex",
            }}
          >
            <Tabs
              orientation="vertical"
              variant="scrollable"
              value={value}
              onChange={handleChange}
              aria-label="Vertical tabs example"
              sx={{ borderRight: 1, borderColor: "divider", width: "20%" }}
            >
              <Tab label="Site" {...a11yProps(0)} />
              <Tab label="Employee" {...a11yProps(1)} />
              <Tab label="Timesheet" {...a11yProps(2)} />
              <Tab label="App" {...a11yProps(3)} />
            </Tabs>
            <TabPanel value={value} index={0}>
              <Grid container>
                <Grid item xs={12}>
                  <Item>
                    <Box
                      component="form"
                      sx={{
                        "& .MuiTextField-root": { ml: 1 },
                      }}
                      noValidate
                      autoComplete="off"
                      onSubmit={handleSiteSubmit(onSiteFormSubmit)}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{ marginBottom: "10px" }}
                        >
                          Site Configuration
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              id="title"
                              label="Title"
                              required
                              InputLabelProps={{
                                shrink: true,
                              }}
                              {...registerSite("site_title")}
                              error={!!siteErrors["site_title"]}
                              helperText={
                                siteErrors["site_title"]
                                  ? siteErrors["site_title"].message
                                  : ""
                              }
                            />
                            <TextField
                              id="contact-email"
                              label="Contact Email Address"
                              required
                              InputLabelProps={{
                                shrink: true,
                              }}
                              {...registerSite("site_contact_email")}
                              error={!!siteErrors["site_contact_email"]}
                              helperText={
                                siteErrors["site_contact_email"]
                                  ? siteErrors["site_contact_email"].message
                                  : ""
                              }
                            />
                            <TextField
                              id="contact-number"
                              label="Contact Number"
                              required
                              InputLabelProps={{
                                shrink: true,
                              }}
                              {...registerSite("site_contact_number")}
                              error={!!siteErrors["site_contact_number"]}
                              helperText={
                                siteErrors["site_contact_number"]
                                  ? siteErrors["site_contact_number"].message
                                  : ""
                              }
                            />
                            <TextField
                              id="contact-address"
                              label="Contact Address"
                              required
                              InputLabelProps={{
                                shrink: true,
                              }}
                              {...registerSite("site_contact_address")}
                              error={!!siteErrors["site_contact_address"]}
                              helperText={
                                siteErrors["site_contact_address"]
                                  ? siteErrors["site_contact_address"].message
                                  : ""
                              }
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mt: 0.05 }}>
                          <Grid item xs={12}>
                            <TextField
                              sx={{ width: "50%" }}
                              id="website"
                              label="Website"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              {...registerSite("site_website")}
                              error={!!siteErrors["site_website"]}
                              helperText={
                                siteErrors["site_website"]
                                  ? siteErrors["site_website"].message
                                  : ""
                              }
                            />
                            <TextField
                              sx={{ width: "37%" }}
                              id="site-logo"
                              label="Logo"
                              type="file"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              {...registerSite("site_logo")}
                              error={!!siteErrors["site_logo"]}
                              helperText={
                                siteErrors["site_logo"]
                                  ? siteErrors["site_logo"].message
                                  : ""
                              }
                            />
                            {siteLogo && (
                              <Box
                                component="img"
                                sx={{
                                  mr: 1,
                                  height: 66,
                                  width: 66,
                                  objectFit: "contain",
                                }}
                                src={siteLogo}
                              />
                            )}
                          </Grid>
                        </Grid>

                        <Grid container spacing={2} sx={{ mt: 0.05 }}>
                          <Grid item xs={12}>
                            <TextField
                              sx={{ width: "60%" }}
                              id="api-resourace-id"
                              label="Public Holiday API Resource ID"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              {...registerSite("api_resource_id")}
                              error={!!siteErrors["api_resource_id"]}
                              helperText={
                                siteErrors["api_resource_id"]
                                  ? siteErrors["api_resource_id"].message
                                  : ""
                              }
                            />

                            <TextField
                              sx={{ width: "37%" }}
                              id="site_session_timeout"
                              label="Session Out Time(min)"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              {...registerSite("site_session_timeout")}
                              error={!!siteErrors["site_session_timeout"]}
                              helperText={
                                siteErrors["site_session_timeout"]
                                  ? siteErrors["site_session_timeout"].message
                                  : ""
                              }
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mt: 0.05 }}>
                          <Grid item xs={12}>
                            <FormLabel sx={{ ml: "10px" }}>
                              Terms & Conditions *
                            </FormLabel>
                            <TextareaAutosize
                              aria-label="termscondition"
                              minRows={3}
                              placeholder="Terms & conditions"
                              style={{
                                width: "98%",
                                marginLeft: "10px",

                                backgroundColor: "transparent",
                                color: theme?.palette?.text?.primary,
                              }}
                              {...registerSite("terms_cond")}
                            />
                          </Grid>
                        </Grid>

                        <Grid container spacing={2} sx={{ mt: 0.05 }}>
                          <Grid item xs={6}>
                            <FormLabel component="legend" sx={{ ml: "10px" }}>
                              Employee Info Metadata*
                            </FormLabel>
                            <TextareaAutosize
                              aria-label="metadata"
                              minRows={3}
                              placeholder="Enter Metadata"
                              style={{
                                width: "100%",
                                marginLeft: "10px",
                                backgroundColor: "transparent",
                                color: theme?.palette?.text?.primary,
                              }}
                              {...registerSite("metadata")}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <FormLabel component="legend" sx={{ ml: "10px" }}>
                              Payroll type*
                            </FormLabel>
                            <TextareaAutosize
                              aria-label="employee_role"
                              minRows={3}
                              placeholder="Enter Payroll type"
                              style={{
                                width: "100%",
                                marginLeft: "10px",
                                backgroundColor: "transparent",
                                color: theme?.palette?.text?.primary,
                              }}
                              {...registerSite("employee_role")}
                            />
                          </Grid>
                        </Grid>
                        <Grid container>
                          <Grid item xs={6}>
                            <FormLabel component="legend" sx={{ ml: "10px" }}>
                              User Roles
                            </FormLabel>
                            <TextareaAutosize
                              aria-label="user_role"
                              minRows={3}
                              placeholder="Enter role type"
                              style={{
                                width: "100%",
                                marginLeft: "10px",
                                backgroundColor: "transparent",
                                color: theme?.palette?.text?.primary,
                              }}
                              {...registerSite("user_role")}
                            />
                          </Grid>
                        </Grid>

                        <Grid item xs={12}>
                          <Controller
                            name="auto_activate_employee"
                            control={siteControl}
                            defaultValue={false}
                            render={({ field }) => (
                              <FormGroup>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      inputProps={{
                                        "aria-label": "auto-activate-employee",
                                      }}
                                      {...field}
                                      checked={field.value}
                                      onChange={(e) =>
                                        field.onChange(e.target.checked)
                                      }
                                    />
                                  }
                                  label="Activate employee"
                                  labelPlacement="start"
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    width: "100%",
                                    maxWidth: "260px",
                                    "& .MuiFormControlLabel-label": {
                                      mr: "1",
                                      marginLeft: "-15px",
                                    },
                                  }}
                                />

                                <FormHelperText sx={{ margin: 0 }}>
                                  Auto activate employee after signup.
                                </FormHelperText>
                              </FormGroup>
                            )}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Controller
                            name="enable_visa_type_info"
                            control={siteControl}
                            defaultValue={false}
                            render={({ field }) => (
                              <FormGroup>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      inputProps={{
                                        "aria-label": "enable-visa-type-info",
                                      }}
                                      {...field}
                                      checked={field.value}
                                      onChange={(e) =>
                                        field.onChange(e.target.checked)
                                      }
                                    />
                                  }
                                  label="Visa Type Info"
                                  labelPlacement="start"
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    width: "100%",
                                    maxWidth: "260px",
                                    "& .MuiFormControlLabel-label": {
                                      mr: "1",
                                      marginLeft: "-15px",
                                    },
                                  }}
                                />

                                <FormHelperText sx={{ margin: 0 }}>
                                  Employee Signup With Visa Type Info.
                                </FormHelperText>
                              </FormGroup>
                            )}
                          />
                        </Grid>

                        <Grid spacing={2} container sx={{ mt: 0.05, ml: 0.05 }}>
                          <Grid item xs={12}>
                            <Box sx={{ m: 1, position: "relative" }}>
                              <Button
                                type="submit"
                                variant="contained"
                                color="success"
                                startIcon={<SaveIcon />}
                                disabled={loading}
                              >
                                Save
                              </Button>
                              {loading && (
                                <CircularProgress
                                  size={24}
                                  sx={{
                                    color: green[500],
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    marginTop: "-12px",
                                    marginLeft: "-12px",
                                  }}
                                />
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Item>
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel value={value} index={1}>
              <Grid container>
                <Grid item xs={12}>
                  <Item>
                    <Box
                      component="form"
                      sx={{
                        "& .MuiTextField-root": { ml: 1 },
                      }}
                      noValidate
                      autoComplete="off"
                      onSubmit={handleEmployeeSubmit(onEmployeeFormSubmit)}
                    >
                      <Box sx={{ mb: 5, width: "100%" }}>
                        <Grid container spacing={2}>
                          <FormControl sx={{ width: 300, mb: 0 }}>
                            <InputLabel shrink>
                              Employee Identification Number *
                            </InputLabel>
                          </FormControl>
                          <Grid item xs={12}>
                            <TextField
                              id="employee-prefix"
                              label="Prefix"
                              {...registerEmployee("employee_prefix")}
                              error={!!employeeErrors["employee_prefix"]}
                              helperText={
                                employeeErrors["employee_prefix"]
                                  ? employeeErrors["employee_prefix"].message
                                  : ""
                              }
                              disabled={
                                employeeDisabledFields &&
                                employeeDisabledFields.includes(
                                  "employee_prefix"
                                )
                              }
                            />
                            <TextField
                              id="employee_idrange"
                              label="Range From"
                              required
                              {...registerEmployee("employee_idrange")}
                              error={!!employeeErrors["employee_idrange"]}
                              helperText={
                                employeeErrors["employee_idrange"]
                                  ? employeeErrors["employee_idrange"].message
                                  : ""
                              }
                              disabled={
                                employeeDisabledFields &&
                                employeeDisabledFields.includes(
                                  "employee_idrange"
                                )
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sx={{ ml: 1 }}>
                            <Box sx={{ m: 1, position: "relative" }}>
                              <Button
                                type="submit"
                                variant="contained"
                                color="success"
                                startIcon={<SaveIcon />}
                                disabled={
                                  employeeDisabledFields.length > 0 || loading
                                }
                              >
                                Save
                              </Button>
                              {loading && (
                                <CircularProgress
                                  size={24}
                                  sx={{
                                    color: green[500],
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    marginTop: "-12px",
                                    marginLeft: "-12px",
                                  }}
                                />
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Item>
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel value={value} index={2}>
              <TimesheetConfiguration
                registerTimesheet={registerTimesheet}
                handleTimesheetSubmit={handleTimesheetSubmit}
                onTimesheetFormSubmit={onTimesheetFormSubmit}
                timesheeErrors={timesheeErrors}
                controlTimesheet={controlTimesheet}
                weekDaysName={weekDaysName}
                loading={loading}
              />
            </TabPanel>
            <TabPanel value={value} index={3}>
              <Grid container>
                <Box
                  component="form"
                  sx={{
                    "& .MuiTextField-root": { ml: 1 },
                  }}
                  noValidate
                  autoComplete="off"
                  onSubmit={handleAppSubmit(onAppFormSubmit)}
                >
                  <Box>
                    <Typography variant="h5" my={3}>
                      App Setting
                    </Typography>

                    <Grid spacing={2} rowGap={2} container>
                      <Grid
                        item
                        display="flex"
                        rowGap={2}
                        flexWrap={"wrap"}
                        xs={12}
                      >
                        <TextField
                          id="primaryColor"
                          label="Primary Color"
                          type="color"
                          required
                          InputLabelProps={{
                            shrink: true,
                          }}
                          sx={{ width: 200 }}
                          {...registerApp("primaryColor")}
                          error={!!appErrors["primaryColor"]}
                          helperText={
                            appErrors["primaryColor"]
                              ? appErrors["primaryColor"].message
                              : ""
                          }
                        />
                        <TextField
                          id="successColor"
                          label="Success Color"
                          required
                          InputLabelProps={{
                            shrink: true,
                          }}
                          type="color"
                          sx={{ width: 200 }}
                          {...registerApp("successColor")}
                          error={!!appErrors["successColor"]}
                          helperText={
                            appErrors["successColor"]
                              ? appErrors["successColor"].message
                              : ""
                          }
                        />
                        <TextField
                          id="errorColor"
                          label="Error Color"
                          required
                          InputLabelProps={{
                            shrink: true,
                          }}
                          type="color"
                          sx={{ width: 200 }}
                          {...registerApp("errorColor")}
                          error={!!appErrors["errorColor"]}
                          helperText={
                            appErrors["errorColor"]
                              ? appErrors["errorColor"].message
                              : ""
                          }
                        />
                        <TextField
                          id="infoColor"
                          label="Info Color"
                          required
                          InputLabelProps={{
                            shrink: true,
                          }}
                          type="color"
                          sx={{ width: 200 }}
                          {...registerApp("infoColor")}
                          error={!!appErrors["infoColor"]}
                          helperText={
                            appErrors["infoColor"]
                              ? appErrors["infoColor"].message
                              : ""
                          }
                        />

                        <TextField
                          id="textColor"
                          label="Text  Color"
                          required
                          InputLabelProps={{
                            shrink: true,
                          }}
                          type="color"
                          sx={{ width: 200 }}
                          {...registerApp("textColor")}
                          error={!!appErrors["textColor"]}
                          helperText={
                            appErrors["textColor"]
                              ? appErrors["textColor"].message
                              : ""
                          }
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box>
                    <Grid spacing={2} container sx={{ mt: 0.05 }}>
                      <Grid
                        item
                        display="flex"
                        rowGap={2}
                        flexWrap={"wrap"}
                        xs={12}
                      >
                        <TextField
                          id="tabBackgroundColor"
                          label="Tab Background Color"
                          required
                          InputLabelProps={{
                            shrink: true,
                          }}
                          type="color"
                          sx={{ width: 200 }}
                          {...registerApp("tabBackgroundColor")}
                          error={!!appErrors["tabBackgroundColor"]}
                          helperText={
                            appErrors["tabBackgroundColor"]
                              ? appErrors["tabBackgroundColor"].message
                              : ""
                          }
                        />

                        <TextField
                          id="tabActiveTextColor"
                          label="Tab Active Color"
                          required
                          InputLabelProps={{
                            shrink: true,
                          }}
                          type="color"
                          sx={{ width: 200 }}
                          {...registerApp("tabActiveTextColor")}
                          error={!!appErrors["tabActiveTextColor"]}
                          helperText={
                            appErrors["tabActiveTextColor"]
                              ? appErrors["tabActiveTextColor"].message
                              : ""
                          }
                        />

                        <TextField
                          id="tabInactiveTextColor"
                          label="Tab Inactive Color"
                          required
                          InputLabelProps={{
                            shrink: true,
                          }}
                          type="color"
                          sx={{ width: 200 }}
                          {...registerApp("tabInactiveTextColor")}
                          error={!!appErrors["tabInactiveTextColor"]}
                          helperText={
                            appErrors["tabInactiveTextColor"]
                              ? appErrors["tabInactiveTextColor"].message
                              : ""
                          }
                        />

                        <TextField
                          id="timerBackgroundColor"
                          label="Timer Background Color"
                          required
                          InputLabelProps={{
                            shrink: true,
                          }}
                          type="color"
                          sx={{ width: 200 }}
                          {...registerApp("timerBackgroundColor")}
                          error={!!appErrors["timerBackgroundColor"]}
                          helperText={
                            appErrors["timerBackgroundColor"]
                              ? appErrors["timerBackgroundColor"].message
                              : ""
                          }
                        />

                        <TextField
                          id="timerTextColor"
                          label="Timer Text Color"
                          required
                          InputLabelProps={{
                            shrink: true,
                          }}
                          type="color"
                          sx={{ width: 200 }}
                          {...registerApp("timerTextColor")}
                          error={!!appErrors["timerTextColor"]}
                          helperText={
                            appErrors["timerTextColor"]
                              ? appErrors["timerTextColor"].message
                              : ""
                          }
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box>
                    <Grid spacing={2} container sx={{ mt: 0.05 }}>
                      <Grid item xs={12}>
                        <TextField
                          id="api_key"
                          label="Push Notification Auth Key"
                          required
                          InputLabelProps={{
                            shrink: true,
                          }}
                          sx={{ width: 600 }}
                          {...registerApp("api_key")}
                          error={!!appErrors["api_key"]}
                          helperText={
                            appErrors["api_key"]
                              ? appErrors["api_key"].message
                              : ""
                          }
                        />

                        <TextField
                          sx={{ width: "30%" }}
                          id="app-logo"
                          label="Logo"
                          type="file"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          {...registerApp("app_logo")}
                          error={!!appErrors["app_logo"]}
                          helperText={
                            appErrors["app_logo"]
                              ? appErrors["app_logo"].message
                              : ""
                          }
                        />

                        {appLogo && (
                          <Box
                            component="img"
                            sx={{
                              mr: 1,
                              height: 66,
                              width: 66,
                              objectFit: "contain",
                            }}
                            src={appLogo}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                  <Box>
                    <Grid spacing={2} container sx={{ mt: 0.05, ml: 0.05 }}>
                      <Grid item xs={12}>
                        <Box sx={{ m: 1, position: "relative" }}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="success"
                            startIcon={<SaveIcon />}
                            disabled={loading}
                          >
                            Save
                          </Button>
                          {loading && (
                            <CircularProgress
                              size={24}
                              sx={{
                                color: green[500],
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                marginTop: "-12px",
                                marginLeft: "-12px",
                              }}
                            />
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Grid>
            </TabPanel>
          </Box>
        </Item>
      </Grid>
    </Grid>
  );
}
